<script setup lang="ts">
    // @ts-ignore
    import { AisInstantSearch, AisInfiniteHits, AisHierarchicalMenu, AisConfigure, AisMenu, AisRefinementList, AisRangeInput, AisCurrentRefinements, AisStats, AisSortBy, AisClearRefinements } from 'vue-instantsearch/vue3/es';
    import type {
        HierarchicalMenuItem,
        HierarchicalMenuRenderState
    } from 'instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu';
    import ProductItemsRenderer from "@/vue/ProductItemsRenderer.vue";
    import Filter from "@/vue/as/Filter.vue";
    import {
        RefinementListRenderState
    } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
    import FilterCheckboxes from "@/vue/as/FilterCheckboxes.vue";
    import { MenuItem, MenuRenderState } from "instantsearch.js/es/connectors/menu/connectMenu";
    import FilterPrice from "@/vue/as/FilterPrice.vue";
    import { Switch } from "@/vue/ui/switch";
    import {
        CurrentRefinementsRenderState
    } from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
    import ChevronDown from '@svg/as/chevron-down.svg';
    import SlidersHorizontal from '@svg/as/sliders-horizontal.svg';
    import { SortByRenderState } from "instantsearch.js/es/connectors/sort-by/connectSortBy";
    import {
        ClearRefinementsConnectorParams,
        ClearRefinementsRenderState
    } from "instantsearch.js/es/connectors/clear-refinements/connectClearRefinements";
    import CurrentRefinementTag from "@/vue/as/CurrentRefinementTag.vue";
    import { StatsRenderState } from "instantsearch.js/es/connectors/stats/connectStats";
    import FilterButton from "@/vue/as/FilterButton.vue";
    import Close from "@svg/as/close.svg";
    import { AccordionRoot } from "radix-vue";
    import { useBreakpoints } from "@/vue/composables/useBreakPoints";
    import { Button } from "@/vue/ui/button";
    import { RangeInputState } from "@/vue/composables/useFilterPrice";
    import { InfiniteHitsState, ProductListProps, useProductList } from "@/vue/composables/useProductList";
    import { vScrollIntoView } from "@/vue/directives/v-scroll-into-view";
    import { vFocus } from "@/vue/directives/v-focus";
    import { RadioGroup, RadioGroupItem } from "@/vue/ui/radio-group";
    import { ref, watchEffect } from "vue";
    import ProductColorCircle from "@/vue/ProductColorCircle.vue";
    import Data from "@/vue/Data.vue";
    import NavTabs from "@/vue/as/NavTabs.vue";
    import NavTab from "@/vue/as/NavTab.vue";

    const props = defineProps<ProductListProps>();

    const scrollContainer = ref<HTMLElement>();
    const {
        ready,
        instantSearchOptions,
        searchParameters,
        infiniteHitsParams,
        categoryMenuParams,
        currentRefinementsParams,
        clearRefinementsParams,
        catalogThemesMenuParams,
        filterInStockForProfileStore,
        filtersMenuVisible,
        filterParams,
        sortByParams,
        currentRefinementLabel,
        currentPriceRefinementLabel,
        productItemsRendererQueryParams,
        hasMoreResults,
        onProductItemsLoaded,
        findColor,
    } = useProductList(props, {
        hitsPerPageFirst: 30 - (props.catalogBlock ? 2 : 0) - 1,
        hitsPerPage: 30,
    });

    const breakpoints = useBreakpoints();

    watchEffect(() => {
        if(breakpoints.md && filtersMenuVisible.value) {
            filtersMenuVisible.value = false;
        }
    });

    function scrollTopIfNeeded() {
        if(scrollContainer.value && scrollContainer.value.getBoundingClientRect().top < 0) {
            scrollContainer.value.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
    }
</script>

<template>
    <div v-show="!ready">
        <slot />
    </div>
    <ais-instant-search v-bind="instantSearchOptions" v-show="ready">
        <ais-configure v-bind="searchParameters" />
        <div class="container max-md:px-4" ref="scrollContainer">
            <div class="lg:grid lg:border-t lg:border-neutral-200 lg:pt-8 lg:grid-cols-[217px_1fr] gap-x-6">
                <div class="">
                    <nav class="mb-4 sticky top-[calc(var(--header-bottom)+2rem)] max-h-[calc(100vh-var(--header-bottom)-2rem)] pb-2 lg:overflow-y-auto lg:overflow-x-hidden">
                        <template v-if="catalog?.themes?.length">
                            <h2 class="text-body-lg font-medium mb-6 hidden lg:block">
                                Thèmes
                            </h2>
                            <ais-menu class="contents" v-bind="catalogThemesMenuParams" v-slot="state: MenuRenderState">
                                <Data v-bind="{ refinedItem: state.items.find(i => i.isRefined) }" v-slot="{ refinedItem }: { refinedItem: MenuItem|null }">
                                    <NavTabs :key="state.items.map(i => i.value).join(',')">
                                        <NavTab
                                            :active="!refinedItem"
                                            :href="refinedItem ? state.createURL(refinedItem.value) : ''"
                                            @click.prevent="refinedItem && state.refine(refinedItem.value)"
                                        >
                                            Tous les thèmes
                                        </NavTab>
                                        <template v-for="item in state.items">
                                            <NavTab :active="item.isRefined"
                                                :href="state.createURL(item.value)"
                                                @click.prevent="state.refine(item.value)"
                                            >
                                                {{ item.label }}
                                            </NavTab>
                                        </template>
                                    </NavTabs>
                                    <div class="overflow-hidden">
                                        <div class="hidden lg:flex flex-col items-start space-y-4">
                                            <a :class="!refinedItem ? 'text-body font-medium': 'text-body-sm hover:underline'"
                                                :href="refinedItem ? state.createURL(refinedItem.value) : ''"
                                                @click.prevent="refinedItem && state.refine(refinedItem.value)"
                                            >
                                                Tous les thèmes
                                            </a>
                                            <template v-for="item in state.items">
                                                <a :class="item.isRefined ? 'text-body font-medium': 'text-body-sm hover:underline'"
                                                    :href="state.createURL(item.value)"
                                                    @click.prevent="state.refine(item.value)"
                                                >
                                                    {{ item.label }}
                                                </a>
                                            </template>
                                        </div>
                                    </div>
                                </Data>
                            </ais-menu>
                        </template>
                        <template v-else>
                            <h2 class="text-body-lg font-medium mb-6 hidden lg:block">
                                Catégories
                            </h2>
                            <ais-hierarchical-menu class="contents" v-bind="categoryMenuParams" v-slot="state: HierarchicalMenuRenderState">
                                <Data v-bind="{
                                    currentItems: state.items.find(i => i.isRefined)?.data ?? state.items,
                                    refinedItem: state.items.find(i => i.isRefined),
                                }" v-slot="{ currentItems, refinedItem }: { currentItems: HierarchicalMenuItem[], refinedItem: HierarchicalMenuItem|null }">
                                    <NavTabs :key="currentItems.map(i => i.value).join(',')">
                                        <template v-for="item in currentItems" :key="item.value">
                                            <NavTab :active="item.isRefined"
                                                :href="state.createURL(item.value)"
                                                @click.prevent="state.refine(item.value)"
                                            >
                                                {{ item.label }}
                                            </NavTab>
                                        </template>
                                    </NavTabs>
                                    <div class="overflow-hidden">
                                        <div class="hidden lg:flex flex-col items-start space-y-4">
                                            <template v-if="!categoryPage">
                                                <a :class="!refinedItem ? 'text-body font-medium': 'text-body-sm hover:underline'"
                                                    :href="refinedItem ? state.createURL(refinedItem.value) : ''"
                                                    @click.prevent="() => {
                                                        if(refinedItem) {
                                                            state.refine(refinedItem.value);
                                                            scrollTopIfNeeded();
                                                        }
                                                    }"
                                                >
                                                    Toutes les catégories
                                                </a>
                                            </template>
                                            <template v-for="item in state.items">
                                                <div>
                                                    <a :href="state.createURL(item.value)"
                                                        class="relative"
                                                        :class="item.isRefined ? 'text-body font-medium': 'text-body-sm hover:underline'"
                                                        @click.prevent="(e) =>{
                                                            if(item.isRefined) {
                                                                const refinedSubItem = item.data?.find(i => i.isRefined);
                                                                if(refinedSubItem) {
                                                                    state.refine(refinedSubItem.value);
                                                                    scrollTopIfNeeded();
                                                                    return;
                                                                }
                                                            }
                                                            state.refine(item.value);
                                                            scrollTopIfNeeded();
                                                        }" v-scroll-into-view="item.isRefined">
                                                        <div class="absolute inset-x-0 -inset-y-2"></div>
                                                        {{ item.label }}
                                                    </a>
                                                    <template v-if="item.isRefined && item.data?.length">
                                                        <div class="flex flex-col pl-4 py-4 space-y-2">
                                                            <template v-for="subItem in item.data">
                                                                <a class="relative"
                                                                    :class="subItem.isRefined ? 'text-body font-medium': 'text-body-sm hover:underline'"
                                                                    :href="state.createURL(subItem.value)"
                                                                    @click.prevent="state.refine(subItem.value); scrollTopIfNeeded()"
                                                                    v-scroll-into-view="item.isRefined"
                                                                >
                                                                    <div class="absolute inset-x-0 -inset-y-1"></div>
                                                                    {{ subItem.label }}
                                                                </a>
                                                            </template>
                                                        </div>
                                                    </template>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </Data>
                            </ais-hierarchical-menu>
                        </template>
                    </nav>
                </div>
                <div>
                    <Teleport to="body" :disabled="breakpoints.md">
                        <div v-show="filtersMenuVisible" class="fixed inset-0 z-10 bg-black/30 md:!hidden" @click="filtersMenuVisible = false"></div>
                        <Transition
                            enter-active-class="transition ease-out duration-100"
                            enter-from-class="translate-x-full"
                            enter-to-class="translate-x-0"
                            leave-active-class="transition ease-out duration-100"
                            leave-from-class="translate-x-0"
                            leave-to-class="translate-x-full"
                        >
                            <div v-show="filtersMenuVisible" class="fixed z-10 flex flex-col inset-0 left-auto bg-white w-full max-w-[400px] overflow-auto md:!contents"
                                :role="breakpoints.md ? 'none' : 'dialog'"
                                aria-label="Trier et filtrer"
                            >
                                <div class="flex-1 container md:px-0">
                                    <div class="flex items-center py-6 md:hidden">
                                        <h4 class="text-body-lg font-medium">
                                            Trier et filtrer
                                        </h4>
                                        <button class="relative ml-auto" v-focus="filtersMenuVisible" aria-label="Fermer" @click="filtersMenuVisible = false" ref="closeBtn">
                                            <Close class="w-6 h-6" />
                                            <span class="absolute -inset-2"></span>
                                        </button>
                                    </div>
                                    <AccordionRoot type="multiple">
                                        <div class="flex flex-col gap-x-3 md:flex-row [&>[class*='--noRefinement']]:hidden">
                                            <ais-sort-by
                                                class="md:hidden"
                                                v-bind="sortByParams"
                                                v-slot="state: SortByRenderState & { items: SortByRenderState['options'] }"
                                            >
                                                <Filter attribute="sort-by" :count="0">
                                                    <template #label>
                                                        Trier
                                                    </template>
                                                    <RadioGroup class="flex flex-col space-y-4" :model-value="state.currentRefinement" @update:model-value="state.refine($event)">
                                                        <template v-for="item in state.items">
                                                            <div class="flex">
                                                                <RadioGroupItem :value="item.value" />
                                                                <div class="ml-2 text-body-sm">
                                                                    {{ item.label }}
                                                                </div>
                                                            </div>
                                                        </template>
                                                    </RadioGroup>
                                                </Filter>
                                            </ais-sort-by>
                                            <template v-if="filterParams['brand']">
                                                <ais-refinement-list v-bind="filterParams['brand']" v-memo="[]" v-slot="state: RefinementListRenderState">
                                                    <Filter attribute="brand" :count="state.items.filter(item => item.isRefined).length">
                                                        <template #label>
                                                            Marque
                                                        </template>
                                                        <FilterCheckboxes :state="state" searchable />
                                                    </Filter>
                                                </ais-refinement-list>
                                            </template>
                                            <ais-refinement-list v-bind="filterParams['material']" v-memo="[]" v-slot="state: RefinementListRenderState">
                                                <Filter attribute="material" :count="state.items.filter(item => item.isRefined).length">
                                                    <template #label>
                                                        Matière
                                                    </template>
                                                    <FilterCheckboxes :state="state" searchable />
                                                </Filter>
                                            </ais-refinement-list>
                                            <ais-menu v-bind="filterParams['color']" v-memo="[]" v-slot="state: MenuRenderState">
                                                <Filter attribute="color" :count="state.items.filter(item => item.isRefined).length">
                                                    <template #label>
                                                        Couleur
                                                    </template>
                                                    <div class="py-6 px-4 pb-0 md:pb-8 min-w-[268px]">
                                                        <div class="max-h-[300px] px-2 overflow-y-auto overflow-x-hidden scrollbar scrollbar-w-[5px] scrollbar-track-rounded-full scrollbar-thumb-rounded-full scrollbar-thumb-neutral-400 scrollbar-track-neutral-100 hover:scrollbar-thumb-neutral-500">
                                                            <div class="grid grid-cols-[repeat(3,auto)] gap-y-4 gap-x-4 justify-between">
                                                                <template v-for="item in state.items">
                                                                    <Data v-bind="{ color: findColor(item.value) }" v-slot="{ color }: { color: ReturnType<typeof findColor> }">
                                                                        <div class="group/item relative flex flex-col items-center">
                                                                            <div class="size-[45px] rounded-full border transition-colors ease-out duration-300 grid place-content-center"
                                                                                :class="item.isRefined ? 'border-neutral-500' : 'border-transparent group-hover/item:border-neutral-500'"
                                                                            >
                                                                                <ProductColorCircle class="relative size-[37px]" :color="color" />
                                                                            </div>
                                                                            <button class="mt-2 text-body-xs text-center text-neutral-900" @click="state.refine(item.value)">
                                                                                <span class="absolute inset-0"></span>
                                                                                {{ item.label }}
                                                                            </button>
                                                                        </div>
                                                                    </Data>
                                                                </template>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Filter>
                                            </ais-menu>
                                            <ais-range-input v-bind="filterParams['price']" v-memo="[]" v-slot="state: RangeInputState">
                                                <Filter attribute="price" :count="state.currentRefinement.min != null || state.currentRefinement.max != null ? 1 : 0">
                                                    <template #label>
                                                        Prix
                                                    </template>
                                                    <FilterPrice :state="state" />
                                                </Filter>
                                            </ais-range-input>
                                            <template v-if="filterParams['decor']">
                                                <ais-refinement-list v-bind="filterParams['decor']" v-memo="[]" v-slot="state: RefinementListRenderState">
                                                    <div></div>
                                                </ais-refinement-list>
                                            </template>
                                            <template v-if="profileStore">
                                                <div class="-order-1 flex items-center py-6 md:order-none md:py-0 md:px-3 md:h-10 ">
                                                    <Switch id="available-in-store" v-model:checked="filterInStockForProfileStore" />
                                                    <label class="ml-2 text-body-sm transition duration-100 ease-out" :class="filterInStockForProfileStore ? 'text-neutral-900' : 'text-neutral-400'" for="available-in-store">
                                                        Produits disponibles à {{ profileStore.name }}
                                                    </label>
                                                </div>
                                            </template>
                                        </div>
                                    </AccordionRoot>
                                </div>
                                <div class="sticky border border-neutral-150 bg-white bottom-0 md:hidden" style="box-shadow: 0 1px 7.6px 0 rgba(0, 0, 0, 0.25)">
                                    <div class="container py-4 flex justify-center space-x-3">
                                        <ais-current-refinements
                                            class="contents"
                                            v-bind="currentRefinementsParams"
                                            v-slot="currentRefinementsState: CurrentRefinementsRenderState"
                                        >
                                            <ais-clear-refinements
                                                class="contents"
                                                v-bind="clearRefinementsParams"
                                                v-slot="clearRefinementsState: ClearRefinementsRenderState"
                                            >
                                                <Button class="flex-1 text-nowrap max-[374px]:text-body-sm !font-semibold !px-2" variant="outline-dark" @click="clearRefinementsState.refine(); filtersMenuVisible = false">
                                                    Tout effacer
                                                    <template v-if="currentRefinementsState.items.length">
                                                        ({{ currentRefinementsState.items.length }})
                                                    </template>
                                                </Button>
                                            </ais-clear-refinements>
                                        </ais-current-refinements>
                                        <Button class="flex-1 text-nowrap max-[374px]:text-body-sm !font-semibold !px-2" variant="primary" @click="filtersMenuVisible = false">
                                            Appliquer
                                        </Button>
                                    </div>
                                </div>
                                <div class="md:hidden" data-focus-trap tabindex="0" @focus="($refs.closeBtn as HTMLButtonElement).focus()"></div>
                            </div>
                        </Transition>
                    </Teleport>

                    <div class="flex flex-col md:block">
                        <ais-current-refinements class="contents" v-bind="currentRefinementsParams" v-slot="state: CurrentRefinementsRenderState">
                            <ul v-show="state.items.length" class="flex items-center py-3 gap-x-4 gap-y-2 md:py-0 md:mt-4" aria-label="Filtres actifs">
                                <template v-for="item in state.items">
                                    <template v-if="item.attribute === 'price'">
                                        <CurrentRefinementTag @clear="item.refinements.forEach(refinement => item.refine(refinement))">
                                            {{ currentPriceRefinementLabel(item) }}
                                        </CurrentRefinementTag>
                                    </template>
                                    <template v-else>
                                        <template v-for="refinement in item.refinements">
                                            <CurrentRefinementTag @clear="item.refine(refinement)">
                                                {{ currentRefinementLabel(refinement) }}
                                            </CurrentRefinementTag>
                                        </template>
                                    </template>
                                </template>
                                <ais-clear-refinements v-bind="clearRefinementsParams" v-once v-slot="state: ClearRefinementsRenderState">
                                    <li>
                                        <button class="relative text-body-sm text-neutral-500 underline hover:text-neutral-900" @click="state.refine()">
                                            <span class="absolute -inset-y-1.5 inset-x-0"></span>
                                            Tout effacer
                                        </button>
                                    </li>
                                </ais-clear-refinements>
                            </ul>
                        </ais-current-refinements>

                        <div class="-order-1 md:mt-4 flex items-center md:order-none">
                            <ais-stats v-slot="state: StatsRenderState">
                                <div class="text-body-sm text-neutral-500">
                                    {{ state.nbHits }} produits
                                </div>
                            </ais-stats>
                            <div class="ml-auto md:hidden">
                                <FilterButton @click="filtersMenuVisible = true">
                                    <SlidersHorizontal class="mr-2 w-4 h-4" />
                                    Trier et Filtrer
                                </FilterButton>
                            </div>
                            <div class="ml-auto hidden md:block">
                                <ais-sort-by
                                    v-bind="sortByParams"
                                    v-slot="state: SortByRenderState & { items: SortByRenderState['options'] }"
                                >
                                    <div v-show="state.canRefine" class="text-body-sm text-neutral-900 flex items-center">
                                        <label for="product-list-sort-by" class="text-body-sm">
                                            Trier par :
                                        </label>
                                        <div class="relative">
                                            <select id="product-list-sort-by" class="appearance-none py-1 px-2 pr-7 outline-none text-body-sm lowercase font-medium text-neutral-900"
                                                :value="state.currentRefinement"
                                                @change="state.refine(($event.target as HTMLSelectElement).value)"
                                            >
                                                <template v-for="item in state.items">
                                                    <option :value="item.value">{{ item.label }}</option>
                                                </template>
                                            </select>
                                            <ChevronDown class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" />
                                        </div>
                                    </div>
                                </ais-sort-by>
                            </div>
                        </div>
                    </div>

                    <template v-if="$slots.alert">
                        <div class="mt-4">
                            <slot name="alert" />
                        </div>
                    </template>
                    <div class="mt-4">
                        <ais-infinite-hits v-bind="infiniteHitsParams" v-slot="state: InfiniteHitsState">
                            <ProductItemsRenderer
                                class="grid max-[375px]:grid-cols-2 grid-cols-[repeat(auto-fill,minmax(160px,1fr))] sm:grid-cols-3 gap-x-2 gap-y-10 md:gap-x-4 md:gap-y-6 xl:gap-6"
                                :items="state.items"
                                :params="productItemsRendererQueryParams(state)"
                                :disabled="filtersMenuVisible"
                                @loaded="onProductItemsLoaded"
                            />
                            <template v-if="hasMoreResults(state)">
                                <div class="mt-24">
                                    <div class="flex justify-center">
                                        <Button variant="outline-dark" @click="state.refineNext()">
                                            Afficher plus
                                        </Button>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <!--  keep space for back to top button -->
                                <div class="h-[4.125rem]"></div>
                            </template>
                        </ais-infinite-hits>
                    </div>
                </div>
            </div>
        </div>
    </ais-instant-search>
</template>
